import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SYNC_UTMB_RUNNERS } from '@/graphql/mutations';

interface SyncUtmbRunnersModalProps {
    raceId: string;
    defaultLiveUrl?: string;
    onClose: () => void;
    onSuccess: () => void;
    toast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const LIVE_URL_REGEX = /^https:\/\/live\.utmb\.world\/([^/]+)\/(\d{4})\/([^/]+)\/?$/;

interface ParsedUrl {
    raceName: string;
    year: string;
    raceId: string;
}

function parseUtmbLiveUrl(url: string): ParsedUrl | null {
    const clean = url.trim().split('?')[0];
    const match = clean.match(LIVE_URL_REGEX);
    if (!match) return null;
    return { raceName: match[1], year: match[2], raceId: match[3] };
}

type SyncState = 'form' | 'confirm' | 'syncing';

export function SyncUtmbRunnersModal({ raceId, defaultLiveUrl, onClose, onSuccess, toast }: SyncUtmbRunnersModalProps) {
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();

    const [liveUrl, setLiveUrl] = useState(defaultLiveUrl ?? '');
    const [runnersPerGender, setRunnersPerGender] = useState(10);
    const [syncState, setSyncState] = useState<SyncState>('form');
    const [urlTouched, setUrlTouched] = useState(false);

    const isSubmitting = useRef(false);

    const parsed = parseUtmbLiveUrl(liveUrl);
    const isUrlValid = parsed !== null;

    const [syncUtmbRunners] = useMutation(SYNC_UTMB_RUNNERS);

    const handleConfirm = () => {
        setSyncState('confirm');
    };

    const handleSync = useCallback(async () => {
        if (isSubmitting.current || !parsed) return;
        isSubmitting.current = true;
        setSyncState('syncing');

        try {
            const { data } = await syncUtmbRunners({
                variables: {
                    input: {
                        raceId,
                        utmbLiveRaceName: parsed.raceName,
                        utmbLiveYear: parsed.year,
                        utmbLiveRaceId: parsed.raceId,
                        runnersPerGender,
                    },
                },
            });

            const res = data?.syncUtmbRunners;
            if (res) {
                if (res.created === 0 && res.skipped === 0) {
                    toast(t('details.syncRunnersModal.noRunners'), 'info');
                } else {
                    toast(t('details.syncRunnersModal.success', { created: res.created, skipped: res.skipped }), 'success');
                    onSuccess();
                }
            }
        } catch {
            toast(t('details.syncRunnersModal.error'), 'error');
        } finally {
            isSubmitting.current = false;
            onClose();
        }
    }, [parsed, raceId, runnersPerGender, syncUtmbRunners, onSuccess, onClose, toast, t]);

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={syncState === 'syncing' ? undefined : onClose}
            />

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm relative z-10 p-6 animate-in zoom-in-95 duration-200 border border-slate-100">
                <div className="flex flex-col items-center text-center gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 bg-blue-50 text-blue-600">
                        {syncState === 'syncing' ? <Loader2 size={24} className="animate-spin" /> : <RefreshCw size={24} />}
                    </div>

                    <h3 className="text-lg font-bold text-slate-900">
                        {t('details.syncRunnersModal.title')}
                    </h3>

                    {/* Syncing state */}
                    {syncState === 'syncing' && (
                        <p className="text-sm text-slate-500">{t('details.syncRunnersModal.syncing')}</p>
                    )}

                    {/* Confirm state */}
                    {syncState === 'confirm' && (
                        <div className="space-y-3 w-full">
                            <p className="text-sm text-slate-500">
                                {t('details.syncRunnersModal.confirmMessage', { count: runnersPerGender })}
                            </p>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => setSyncState('form')}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
                                >
                                    {tc('btn.back')}
                                </button>
                                <button
                                    onClick={handleSync}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 active:scale-95"
                                >
                                    {tc('btn.confirm')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Form state */}
                    {syncState === 'form' && (
                        <div className="space-y-4 w-full text-left">
                            {/* URL input */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    {t('details.syncRunnersModal.liveUrl')}
                                </label>
                                <input
                                    type="url"
                                    value={liveUrl}
                                    onChange={(e) => { setLiveUrl(e.target.value); setUrlTouched(true); }}
                                    placeholder={t('details.syncRunnersModal.liveUrlPlaceholder')}
                                    className={cn(
                                        "w-full px-3 py-2.5 rounded-xl border text-sm transition-colors focus:outline-none focus:ring-2",
                                        urlTouched && liveUrl && !isUrlValid
                                            ? "border-red-300 focus:ring-red-200"
                                            : "border-slate-200 focus:ring-blue-200"
                                    )}
                                />
                                {urlTouched && liveUrl && !isUrlValid && (
                                    <p className="text-xs text-red-500 mt-1">
                                        {t('details.syncRunnersModal.liveUrlInvalid')}
                                    </p>
                                )}
                            </div>

                            {/* Parsed values preview */}
                            {parsed && (
                                <div className="bg-slate-50 rounded-xl p-3 text-xs space-y-1">
                                    <p className="font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                                        {t('details.syncRunnersModal.parsedValues')}
                                    </p>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">{t('details.syncRunnersModal.raceName')}</span>
                                        <span className="font-mono text-slate-700">{parsed.raceName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">{t('details.syncRunnersModal.year')}</span>
                                        <span className="font-mono text-slate-700">{parsed.year}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-400">{t('details.syncRunnersModal.raceId')}</span>
                                        <span className="font-mono text-slate-700">{parsed.raceId}</span>
                                    </div>
                                </div>
                            )}

                            {/* Runners per gender */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                    {t('details.syncRunnersModal.runnersPerGender')}
                                </label>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range"
                                        min={1}
                                        max={30}
                                        value={runnersPerGender}
                                        onChange={(e) => setRunnersPerGender(Number(e.target.value))}
                                        className="flex-1 accent-blue-600"
                                    />
                                    <span className="text-sm font-bold text-slate-700 w-8 text-right tabular-nums">
                                        {runnersPerGender}
                                    </span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 w-full pt-2">
                                <button
                                    onClick={onClose}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
                                >
                                    {tc('btn.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    disabled={!isUrlValid}
                                    className={cn(
                                        "flex-1 py-3 px-4 rounded-xl font-bold text-white transition-colors shadow-lg active:scale-95",
                                        isUrlValid
                                            ? "bg-blue-600 hover:bg-blue-700 shadow-blue-200"
                                            : "bg-slate-300 cursor-not-allowed shadow-none"
                                    )}
                                >
                                    {tc('btn.continue')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
